import { useCallback, useState } from "react";
import { Box, Button, Container, InputText, Icon, Stack, Text } from "@bookingcom/bui-react";
import { ArrowNavLeftIcon, MagnifyingGlassIcon } from "@bookingcom/bui-assets-react/streamline";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { useNavigate } from "@tanstack/react-router";
import { LoadingSpinner } from "../LoadingSpinner";
import { UserManagementErrorState } from "./UserManagementErrorState";
import { UserManagementTable } from "./UserManagementTable";
import { useUsersQuery } from "../../hooks/useUsersQuery";
import { useDebounce } from "../../hooks/useDebounce";
import styles from "./UserManagementPage.module.css";

export const UserManagementPage = () => {
  const i18n = useI18n();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(0);

  const debouncedSearch = useDebounce(searchInput, 300);
  const isSearchTooShort = debouncedSearch.length > 0 && debouncedSearch.length < 3;

  const { data, isLoading, isError, refetch } = useUsersQuery({
    search: isSearchTooShort ? undefined : debouncedSearch || undefined,
    page,
  });

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    setPage(0);
  }, []);

  return (
    <Container>
      <Box
        mixin={{
          paddingBlockStart: 8,
          paddingBlockEnd: 8,
          paddingInlineStart: 0,
          paddingInlineEnd: 0,
        }}
      >
        <Stack gap={8}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack gap={0}>
              <Text variant="headline_1">User Management</Text>
              <Text variant="body_2" color="neutral_alt">
                Manage who has access to the Airline Portal. Invite colleagues from your group or participating
                airlines.
              </Text>
            </Stack>
            <Button variant="secondary" icon={ArrowNavLeftIcon} onClick={() => navigate({ to: "/overview" })}>
              {i18n.trans(t("flights_airline_portal_superuser_back_button"))}
            </Button>
          </Stack>

          <Stack gap={6}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <div className={styles.searchContainer}>
                <InputText
                  name="search"
                  startIcon={<Icon svg={MagnifyingGlassIcon} />}
                  placeholder="Search by name, email or airline..."
                  value={searchInput}
                  onChange={({ value }) => handleSearchChange(value)}
                  error={isSearchTooShort ? "Enter at least 3 characters to search" : undefined}
                />
              </div>
              <Button variant="primary" onClick={() => navigate({ to: "/add-user" })}>
                + Add user
              </Button>
            </Stack>

            {isLoading && !data ? (
              <LoadingSpinner />
            ) : isError ? (
              <UserManagementErrorState onRetry={() => refetch()} />
            ) : (
              <UserManagementTable
                users={data?.users ?? []}
                page={data?.page ?? 0}
                totalCount={data?.totalCount ?? 0}
                size={data?.size ?? 10}
                onPageChange={setPage}
              />
            )}
          </Stack>
        </Stack>
      </Box>
    </Container>
  );
};
